import { gsap } from "gsap";
import { type RefObject, useLayoutEffect } from "react";

import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";

/**
 * Runs an illustration's looping GSAP timeline while the drawing is on screen. `build` receives the SVG and
 * returns the timeline (created paused-agnostic; this hook decides when it plays). The loop pauses while the
 * SVG is scrolled out of view and is reverted on unmount, so a dialog that closes leaves nothing running.
 *
 * With reduced motion nothing is built: the markup must already be its own static end state.
 *
 * `build` must be a stable, module-level function — it is not a dependency of the effect.
 */
export function useIllustrationLoop(
  ref: RefObject<SVGSVGElement | null>,
  build: (svg: SVGSVGElement) => gsap.core.Timeline,
): void {
  const reduced = usePrefersReducedMotion();

  useLayoutEffect(() => {
    const svg = ref.current;
    if (!svg || reduced) return;

    let timeline: gsap.core.Timeline | null = null;
    const ctx = gsap.context(() => {
      timeline = build(svg);
    }, svg);

    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) timeline?.play();
        else timeline?.pause();
      });
      observer.observe(svg);
    }

    return () => {
      observer?.disconnect();
      ctx.revert();
    };
    // `build` is module-level by contract; re-running on its identity would restart the loop every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, reduced]);
}
